import React, { useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf } from '@fortawesome/pro-regular-svg-icons';
import { Badge } from '@buildoutinc/blueprint-react/ui/Badge';
import { Card } from '@buildoutinc/blueprint-react/ui/Card';
import { Modal } from '@buildoutinc/blueprint-react/ui/Modal';

import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import ImageDropzone from 'components/image_dropzone';
import { post } from 'helpers/api';

interface UploadResponse {
  id: number;
  url: string;
  file_name: string;
}

// helpers/api resolves to the camelCased response body itself, not an axios response.
interface GenerateResponse {
  bioPdfSource: string;
  fileName: string | null;
  updatedAt: string | null;
  url: string | null;
}

interface Props {
  bioPdfSource: string;
  uploadedPdfUrl: string | null;
  generatedPdfUrl: string | null;
  generatedPdfUpdatedAt: string | null;
  uploadedPdfUpdatedAt: string | null;
  uploadedFileName: string | null;
  generatedFileName: string | null;
  previewPath: string | null;
  // Null on the New User form, where the broker does not exist yet. previewPath is null there too,
  // so the template option is not offered and this is never used.
  generatePath: string | null;
}

const TEMPLATE = 'template';
const UPLOAD = 'upload';

// The thumbnail is the document itself, shrunk down - the same page the preview and the PDF use.
const THUMBNAIL_WIDTH = 96;
const THUMBNAIL_HEIGHT = 124;
const THUMBNAIL_PAGE_WIDTH = 900;
const THUMBNAIL_SCALE = THUMBNAIL_WIDTH / THUMBNAIL_PAGE_WIDTH;

// Accent tint used for the template option and its note, matching the designed prototype.
const TINT = '#f4f1fe';

export default function BioPdfUpload({
  bioPdfSource,
  uploadedPdfUrl,
  generatedPdfUrl,
  generatedPdfUpdatedAt,
  uploadedPdfUpdatedAt,
  uploadedFileName,
  generatedFileName,
  previewPath,
  generatePath,
}: Props) {
  const [source, setSource] = useState(bioPdfSource);
  const [generated, setGenerated] = useState({
    url: generatedPdfUrl,
    fileName: generatedFileName,
    updatedAt: generatedPdfUpdatedAt,
  });
  const [uploadId, setUploadId] = useState<number | null>(null);
  const [newFileName, setNewFileName] = useState<string | null>(null);
  const [removed, setRemoved] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Files uploaded before this setting existed report source "none", so an existing file still counts.
  const hasUpload = Boolean(uploadedPdfUrl) && source !== TEMPLATE;
  const showTemplateCard = source === TEMPLATE && !removed;
  const showUploadCard = (hasUpload || Boolean(uploadId)) && !removed;
  const showChoice = !showTemplateCard && !showUploadCard;

  function handleUpload(data: UploadResponse) {
    setUploadId(data.id);
    setNewFileName(data.file_name);
    setSource(UPLOAD);
    setRemoved(false);
    setError(null);
  }

  async function useTemplate(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    setError(null);
    setGenerating(true);

    try {
      const data: GenerateResponse = await post(generatePath);

      setGenerated({ url: data.url, fileName: data.fileName, updatedAt: data.updatedAt });
      setSource(TEMPLATE);
      setUploadId(null);
      setNewFileName(null);
      setRemoved(false);
      setPreviewOpen(false);
    } catch {
      setError("We couldn't build the bio right now. Please try again.");
    } finally {
      setGenerating(false);
    }
  }

  function openPreview(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    setPreviewOpen(true);
  }

  function closePreview() {
    setPreviewOpen(false);
  }

  function remove(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    setRemoved(true);
    setUploadId(null);
    setNewFileName(null);
  }

  return (
    <div>
      {error && <Alert severity="destructive">{error}</Alert>}

      {showChoice && (
        <div className="row">
          {previewPath && (
            <div className="col-md-6 mb-3">
              <Card className="h-100 border-primary border-opacity-25">
                <Card.Body className="h-100 rounded" style={{ backgroundColor: TINT }}>
                  <strong className="d-block mb-2">Use the company bio template</strong>
                  <p className="text-muted small">
                    Builds a branded bio from the information already on your profile. It stays current -
                    every time you save your profile, the bio updates to match.
                  </p>
                  <Button label="Preview bio" variant="primary" onClick={openPreview} />
                </Card.Body>
              </Card>
            </div>
          )}
          <div className={previewPath ? 'col-md-6 mb-3' : 'col-12 mb-3'}>
            <Card className="h-100">
              <Card.Body>
                <strong className="d-block mb-2">Upload your own PDF</strong>
                {/* Without the company template there is nothing to compare the upload to, so the copy
                    must not mention it. */}
                <p className="text-muted small">
                  Use a file you supply instead. It won&apos;t update when your profile changes
                  {previewPath ? ', and it replaces the template.' : '.'}
                </p>
                <ImageDropzone
                  allowedMedia={['application/pdf']}
                  dropBox={
                    <div className="p-3 text-center text-muted">
                      <FontAwesomeIcon className="me-1" icon={faFilePdf} />
                      Drag a PDF, or click here to upload
                    </div>
                  }
                  dropZoneClassName="media-uploader"
                  onUploadComplete={handleUpload}
                />
              </Card.Body>
            </Card>
          </div>
        </div>
      )}

      {showTemplateCard && (
        <>
          <Card>
            <Card.Body className="d-flex gap-3">
              {previewPath && (
                <div
                  className="border rounded overflow-hidden flex-shrink-0"
                  style={{ width: THUMBNAIL_WIDTH, height: THUMBNAIL_HEIGHT }}
                >
                  <iframe
                    scrolling="no"
                    src={previewPath}
                    style={{
                      width: THUMBNAIL_PAGE_WIDTH,
                      height: THUMBNAIL_HEIGHT / THUMBNAIL_SCALE,
                      border: 0,
                      transform: `scale(${THUMBNAIL_SCALE})`,
                      transformOrigin: 'top left',
                      pointerEvents: 'none',
                    }}
                    tabIndex={-1}
                    title="Bio thumbnail"
                  />
                </div>
              )}
              <div className="flex-grow-1">
                <div className="d-flex align-items-center gap-2">
                  <strong>{generated.fileName}</strong>
                  <Badge className="bg-success-subtle text-success border-0">KEPT UP TO DATE</Badge>
                  <Badge className="text-muted" variant="outline">READ-ONLY</Badge>
                </div>
                <p className="text-muted small mb-3 mt-1">
                  Company bio template{generated.updatedAt ? ` · Last updated ${generated.updatedAt}` : ''}
                  <br />
                  Live on your public profile as <strong>Download Bio</strong>
                </p>
                <div className="d-flex flex-wrap gap-2">
                  {generated.url && (
                    <Button
                      href={generated.url}
                      label="View bio"
                      size="sm"
                      target="_blank"
                      variant="primary"
                    />
                  )}
                  <Button label="Upload my own PDF instead" size="sm" variant="outline" onClick={remove} />
                  <Button
                    className="text-danger border-danger"
                    label="Remove"
                    size="sm"
                    variant="outline"
                    onClick={remove}
                  />
                </div>
              </div>
            </Card.Body>
          </Card>
          <Card className="mt-3 border-primary border-opacity-25">
            <Card.Body className="small rounded" style={{ backgroundColor: TINT }}>
            Saving your profile rebuilds this bio, so the version on the website always matches your
            current details. It stays read-only here and on the website - adding it to a property document
            creates an editable copy that doesn&apos;t affect the website version.
            </Card.Body>
          </Card>
        </>
      )}

      {showUploadCard && (
        <>
          <Card>
            <Card.Body className="d-flex gap-3">
              <div
                className="border rounded d-flex align-items-center justify-content-center flex-shrink-0 bg-light"
                style={{ width: THUMBNAIL_WIDTH, height: THUMBNAIL_HEIGHT }}
              >
                <FontAwesomeIcon className="text-primary fa-2x" icon={faFilePdf} />
              </div>
              <div className="flex-grow-1">
                <div className="d-flex align-items-center gap-2">
                  <strong>{newFileName ?? uploadedFileName}</strong>
                  <Badge className="text-muted" variant="outline">READ-ONLY</Badge>
                  {uploadId && <span className="text-danger small">(update user to save)</span>}
                </div>
                <p className="text-muted small mb-3 mt-1">
                  Uploaded file{!uploadId && uploadedPdfUpdatedAt ? ` · ${uploadedPdfUpdatedAt}` : ''}
                  <br />
                  Live on your public profile as <strong>Download Bio</strong>
                </p>
                <div className="d-flex flex-wrap gap-2">
                  {!uploadId && uploadedPdfUrl && (
                    <Button
                      href={uploadedPdfUrl}
                      label="View file"
                      size="sm"
                      target="_blank"
                      variant="primary"
                    />
                  )}
                  {previewPath && (
                    <Button
                      disabled={generating}
                      label="Use the company template instead"
                      size="sm"
                      variant="outline"
                      onClick={openPreview}
                    />
                  )}
                  <Button
                    className="text-danger border-danger"
                    label="Remove"
                    size="sm"
                    variant="outline"
                    onClick={remove}
                  />
                </div>
              </div>
            </Card.Body>
          </Card>
          {previewPath && (
            <Card className="mt-3 border-primary border-opacity-25">
              <Card.Body className="small rounded" style={{ backgroundColor: TINT }}>
              Uploaded files don&apos;t update when your profile changes. Switch to the company template if
              you want the bio to stay in sync.
              </Card.Body>
            </Card>
          )}
        </>
      )}

      {previewPath && (
        <Modal open={previewOpen} onClose={closePreview}>
          <Modal.Content>
            <Modal.Header>
              <div>
                <Modal.Title>Preview bio</Modal.Title>
                <div className="text-muted mt-1">Generated from this profile.</div>
              </div>
            </Modal.Header>
            <Modal.Body>
              <iframe className="w-100 border" src={previewPath} style={{ height: '65vh' }} title="Bio preview" />
            </Modal.Body>
            <Modal.Footer>
              <Button label="Cancel" variant="outline" onClick={closePreview} />
              <Button
                disabled={generating}
                label={generating ? 'Building...' : 'Use this bio'}
                variant="primary"
                onClick={useTemplate}
              />
            </Modal.Footer>
          </Modal.Content>
        </Modal>
      )}

      {uploadId && <input name="user[bio_pdf_upload_id]" type="hidden" value={uploadId} />}
      <input name="user[delete_bio_pdf]" type="hidden" value={removed && source !== TEMPLATE ? 'true' : 'false'} />
      <input
        name="user[delete_generated_bio_pdf]"
        type="hidden"
        value={removed && source === TEMPLATE ? 'true' : 'false'}
      />
    </div>
  );
}
